import { Button } from '@/ui/button';
import { MaterialIcon } from '@/ui/material-icon';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/ui/select';
import styles from './FloatingToolbar.module.scss';

const ZOOM_PRESETS = [50, 75, 100, 125, 150];

interface FloatingToolbarProps {
  zoomPct: number;
  setZoomPct: (pct: number) => void;
  canvasLocked: boolean;
  handleFit: () => void;
  handleUndo: () => void;
  handleRedo: () => void;
  handleToggleLock: () => void;
  handleZoomOut: () => void;
  handleZoomIn: () => void;
  getViewport: () => { x: number; y: number; zoom: number };
  setViewport: (viewport: { x: number; y: number; zoom: number }) => void;
  undoStackLength: number;
  redoStackLength: number;
}

const FloatingToolbar = ({
  zoomPct,
  setZoomPct,
  canvasLocked,
  handleFit,
  handleUndo,
  handleRedo,
  handleToggleLock,
  handleZoomOut,
  handleZoomIn,
  getViewport,
  setViewport,
  undoStackLength,
  redoStackLength,
}: FloatingToolbarProps) => {
  return (
    <div className={styles.floatingToolbar}>
      <Button type="button" variant="secondary" size="icon" aria-label="Fit view" onClick={handleFit}>
        <MaterialIcon name="refresh" size={20} />
      </Button>

      <Button
        type="button"
        variant="secondary"
        size="icon"
        aria-label="Undo"
        onClick={handleUndo}
        disabled={undoStackLength === 0}
      >
        <MaterialIcon name="undo" size={20} />
      </Button>

      <Button
        type="button"
        variant="secondary"
        size="icon"
        aria-label="Redo"
        onClick={handleRedo}
        disabled={redoStackLength === 0}
      >
        <MaterialIcon name="redo" size={20} />
      </Button>

      <Button
        type="button"
        variant="secondary"
        size="icon"
        aria-label={canvasLocked ? 'Unlock canvas' : 'Lock canvas'}
        onClick={handleToggleLock}
        className={canvasLocked ? 'bg-muted text-foreground' : ''}
      >
        <MaterialIcon name={canvasLocked ? 'lock' : 'lock_open'} size={22} />
      </Button>

      <Button
        type="button"
        variant="secondary"
        size="icon"
        aria-label="Zoom out"
        onClick={handleZoomOut}
      >
        <MaterialIcon name="remove" size={20} />
      </Button>

      <Select
        value={String(zoomPct)}
        onValueChange={(val) => {
          if (canvasLocked) return;
          const z = Number(val) / 100;
          const v = getViewport();
          setViewport({ x: v.x, y: v.y, zoom: z });
          setZoomPct(Number(val));
        }}
        disabled={canvasLocked}
      >
        <SelectTrigger className="h-8 w-[72px] text-xs" aria-label="Zoom level">
          <SelectValue>{zoomPct}%</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {ZOOM_PRESETS.map((preset) => (
            <SelectItem key={preset} value={String(preset)}>
              {preset}%
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button
        type="button"
        variant="secondary"
        size="icon"
        aria-label="Zoom in"
        onClick={handleZoomIn}
      >
        <MaterialIcon name="add" size={20} />
      </Button>
    </div>
  );
};

export default FloatingToolbar;
